// src/services/fteDaysOffReportService.ts
//
// Builds the "FTE & Requested Days Off" planning report for a collection year.
//
// Two facts are joined per staff member:
//   - CURRENT contracted FTE, straight off the staff record (pit.staff.fte).
//   - REQUESTED load for next year, from their teaching-intention submission
//     (availability.ftePreference) plus which weekday(s) they asked to have off
//     (availability.dayOffPreference).
//
// teaching_intentions.staff_id holds the 3-letter staff CODE, not a UUID, so the
// roster join is by code (staff_code → initial), with a name fallback for the
// handful of imported rows whose code doesn't match the roster.
//
// Staff who have not responded are still returned (hasResponse = false) because
// an unanswered load is a planning gap, not an absence of data.

import { staffService, StaffMember } from './staffService';
import { intentionsService } from './intentionsService';
import { TeachingIntention } from '../types/intentions';
import { AvailabilityData, DayOffPreference, FtePreference } from '../types/intentionPeriods';
import { getStaffCode, isExpectedToSubmit, isSubmittedStatus } from '../utils/intentionStats';

export type Weekday = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri';

export const WEEKDAYS: Weekday[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * Which weekday(s) each day-off answer maps to. 'none' (no preference) and
 * 'discuss' name no day — the person still needs days off, they just haven't
 * said which, so those land in the "not yet named" pool instead.
 */
const DAY_OFF_DAYS: Partial<Record<DayOffPreference, Weekday[]>> = {
  'wed-thu': ['Wed', 'Thu'],
  'mon-fri': ['Mon', 'Fri'],
  'wed-only': ['Wed'],
  'fri-only': ['Fri'],
};

const DAY_OFF_LABELS: Record<DayOffPreference, string> = {
  'wed-thu': 'Wednesday & Thursday',
  'mon-fri': 'Monday & Friday',
  'wed-only': 'Wednesday',
  'fri-only': 'Friday',
  none: 'No preference',
  discuss: 'Wants to discuss',
};

/**
 * Days off per week implied by each load option. Taken from the wording staff
 * see on the intention form (AvailabilitySection), not from arithmetic — 0.75
 * is described to them as one day off, not 1.25.
 */
const DAYS_OFF_BY_FTE: Record<FtePreference, number | null> = {
  '1': 0,
  '0.8': 1,
  '0.75': 1,
  '0.6': 2,
  '0.5': 2,
  '0.4': 3,
  discuss: null, // unknown until the conversation happens
};

const FTE_NUMERIC: Record<FtePreference, number | null> = {
  '1': 1,
  '0.8': 0.8,
  '0.75': 0.75,
  '0.6': 0.6,
  '0.5': 0.5,
  '0.4': 0.4,
  discuss: null,
};

const FTE_LABELS: Record<FtePreference, string> = {
  '1': 'Full time (1.0)',
  '0.8': '0.8',
  '0.75': '0.75',
  '0.6': '0.6',
  '0.5': '0.5',
  '0.4': '0.4',
  discuss: 'Wants to discuss',
};

/** Display order for the requested-load bands (highest load first). */
export const FTE_BAND_ORDER: FtePreference[] = ['1', '0.8', '0.75', '0.6', '0.5', '0.4', 'discuss'];

export interface FteDaysOffRow {
  /** Stable row key: staff UUID when matched to the roster, else the code. */
  key: string;
  staffId: string | null;
  staffCode: string;
  staffName: string;
  email: string;
  department: string;
  employmentType: string;
  role: string;

  /** Contracted FTE today, from the staff record. */
  currentFte: number | null;
  /** Requested FTE for next year. null when they chose "discuss" or didn't respond. */
  requestedFte: number | null;
  ftePreference: FtePreference | null;
  fteLabel: string;
  /** requestedFte - currentFte. null when either side is unknown. */
  fteDelta: number | null;

  /** Weekday(s) they asked to have off. Empty when none named. */
  daysOffRequested: Weekday[];
  daysOffLabel: string;
  dayOffPreference: DayOffPreference | null;
  /** Days off per week implied by the requested load. null when "discuss". */
  daysOffEntitled: number | null;
  /** Days off they actually named (0 when "no preference"/"discuss"). */
  daysOffNamed: number;
  /** Days off owed that have no weekday against them yet. */
  daysOffUnnamed: number;
  /**
   * Asked for a part-time load but named no weekday at all — answered "no
   * preference", asked to discuss it, or skipped the question. The planning gap
   * this flags: the load is settled, the day isn't. Submitted responses only,
   * because a draft is not an answer.
   */
  awaitingDayChoice: boolean;

  /** Chose "discuss" on load or day off — needs a conversation before planning. */
  needsDiscussion: boolean;
  returning: 'yes' | 'no' | 'uncertain' | null;
  hasLeavePlans: boolean;
  leaveType: string | null;
  leaveStartDate: string | null;
  leaveEndDate: string | null;
  leavePlans: string;
  comments: string;

  submissionStatus: string | null;
  hasResponse: boolean;
  isLate: boolean;
  submittedAt: Date | null;
}

export interface FteBandCount {
  preference: FtePreference;
  label: string;
  count: number;
  /** Sum of the requested FTE in this band (0 for "discuss"). */
  fte: number;
}

export interface WeekdayDemand {
  day: Weekday;
  /** Staff who named this weekday as a day off. */
  count: number;
}

export interface DepartmentSummary {
  department: string;
  staffCount: number;
  respondedCount: number;
  currentFte: number;
  /** Requested FTE for responders with a known load (excludes "discuss"). */
  requestedFte: number;
  /** requestedFte - currentFte over the same comparable staff. */
  fteDelta: number;
  /** Total days off per week the department's staff have asked for. */
  daysOffDemand: number;
  /** Part-time requests in this area with no weekday named at all. */
  noDayNamedCount: number;
  needsDiscussionCount: number;
  notReturningCount: number;
  noResponseCount: number;
}

export interface FteDaysOffSummary {
  staffCount: number;
  respondedCount: number;
  noResponseCount: number;
  draftOnlyCount: number;

  /** Contracted FTE across every staff member shown. */
  currentFteTotal: number;
  /** Requested FTE across responders with a known load. */
  requestedFteTotal: number;
  /** Their current FTE, so the two totals compare like for like. */
  comparableCurrentFteTotal: number;
  /** requestedFteTotal - comparableCurrentFteTotal. */
  fteDeltaTotal: number;

  reducingCount: number;
  increasingCount: number;
  unchangedCount: number;
  unknownFteCount: number;

  needsDiscussionCount: number;
  notReturningCount: number;
  uncertainCount: number;
  leaveCount: number;
  lateCount: number;

  /** Total days off per week requested across all staff shown. */
  totalDaysOffRequested: number;
  /** Days off owed with no weekday named yet (the planning unknown). */
  unnamedDaysOffTotal: number;
  /** Staff who asked for a part-time load but named no weekday at all. */
  noDayNamedCount: number;
  /** Days off per week those staff are owed — the unplaced part of the above. */
  noDayNamedDaysTotal: number;
  /** Staff who named some, but not all, of the days off their load implies. */
  partlyNamedCount: number;

  fteBands: FteBandCount[];
  weekdayDemand: WeekdayDemand[];
  byDepartment: DepartmentSummary[];
}

export interface FteDaysOffReport {
  year: number;
  rows: FteDaysOffRow[];
  summary: FteDaysOffSummary;
  /**
   * How many staff existed BEFORE the roster filters ran.
   *
   * An empty report has two very different causes and the page could not tell
   * them apart: filters excluded everyone, or the school has no staff at all.
   * A Marian College session hit the second and it was escalated as a broken
   * report — that tenant has one staff row (an admin), the report defaults to
   * teaching roles, so the roster came out empty and the message said "try a
   * different filter", which could never have helped (issue #152).
   */
  rosterSourceCount: number;
  /** Staff the group filter alone would have kept, before department narrowing. */
  groupMatchCount: number;
}

export interface FteDaysOffOptions {
  year: number;
  /** Learning area / department name. undefined or 'all' => no filter. */
  department?: string;
  /** Who is expected to respond: teaching roles only (default) or all active staff. */
  staffGroup?: 'teaching' | 'all';
  /** Include staff with no intention submission. Default true. */
  includeNonResponders?: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Normalise a code the way intentionStats does, so both sides of the join agree. */
const normCode = (v: unknown): string => (v ?? '').toString().trim().toUpperCase();

/**
 * Read the availability answers off an intention. App-submitted rows carry a
 * structured `availability` column; older imported rows have nothing, which is
 * why every field here is optional.
 */
const readAvailability = (intention: TeachingIntention | undefined): AvailabilityData | null => {
  const a = intention?.availability;
  if (!a || typeof a !== 'object') return null;
  return a as AvailabilityData;
};

/**
 * Pick one intention per staff code: a submitted row always beats a draft, and
 * among equals the most recently updated wins. A person can save a draft and
 * later submit, leaving two rows for the same year.
 */
function dedupeByStaffCode(intentions: TeachingIntention[]): Map<string, TeachingIntention> {
  const byCode = new Map<string, TeachingIntention>();
  for (const intention of intentions) {
    const code = normCode(intention.staffId);
    if (!code) continue;
    const existing = byCode.get(code);
    if (!existing) {
      byCode.set(code, intention);
      continue;
    }
    const existingSubmitted = isSubmittedStatus(existing.status);
    const candidateSubmitted = isSubmittedStatus(intention.status);
    if (candidateSubmitted && !existingSubmitted) {
      byCode.set(code, intention);
      continue;
    }
    if (candidateSubmitted === existingSubmitted) {
      const existingAt = existing.lastUpdatedAt?.getTime?.() ?? 0;
      const candidateAt = intention.lastUpdatedAt?.getTime?.() ?? 0;
      if (candidateAt > existingAt) byCode.set(code, intention);
    }
  }
  return byCode;
}

/** Build one report row from a roster member and their (optional) intention. */
function buildRow(staff: StaffMember, intention: TeachingIntention | undefined): FteDaysOffRow {
  const availability = readAvailability(intention);
  const hasResponse = !!intention && isSubmittedStatus(intention.status);

  const ftePreference = (availability?.ftePreference ?? null) as FtePreference | null;
  const dayOffPreference = (availability?.dayOffPreference ?? null) as DayOffPreference | null;

  // Prefer the coded answer. Older rows only carry the numeric `fte`, so fall
  // back to it rather than showing nothing.
  const requestedFte =
    ftePreference != null
      ? FTE_NUMERIC[ftePreference]
      : typeof availability?.fte === 'number'
      ? availability.fte
      : null;

  const currentFte = typeof staff.fte === 'number' ? staff.fte : null;
  const fteDelta =
    requestedFte != null && currentFte != null ? round2(requestedFte - currentFte) : null;

  const daysOffRequested = dayOffPreference ? DAY_OFF_DAYS[dayOffPreference] ?? [] : [];
  const daysOffEntitled =
    ftePreference != null
      ? DAYS_OFF_BY_FTE[ftePreference]
      : requestedFte != null
      ? Math.max(0, Math.round((1 - requestedFte) * 5))
      : null;
  const daysOffNamed = daysOffRequested.length;
  const daysOffUnnamed =
    daysOffEntitled == null ? 0 : Math.max(0, daysOffEntitled - daysOffNamed);

  // Part-time load agreed, no weekday against it. Kept as a row-level flag so the
  // list filter, the summary list and the export all read the same rule.
  const awaitingDayChoice = hasResponse && (daysOffEntitled ?? 0) > 0 && daysOffNamed === 0;

  const daysOffLabel = dayOffPreference
    ? DAY_OFF_LABELS[dayOffPreference] ?? dayOffPreference
    : daysOffEntitled && daysOffEntitled > 0
    ? 'Not specified'
    : '—';

  const employment = intention?.employmentDetails;

  return {
    key: staff.id || normCode(getStaffCode(staff)),
    staffId: staff.id || null,
    staffCode: getStaffCode(staff),
    staffName: staff.name,
    email: staff.email || '',
    department: staff.department || intention?.department || 'Unknown',
    employmentType: staff.employment_type || employment?.employmentType || '—',
    role: staff.role || 'teacher',

    currentFte,
    requestedFte,
    ftePreference,
    fteLabel: ftePreference
      ? FTE_LABELS[ftePreference]
      : requestedFte != null
      ? requestedFte.toFixed(2)
      : hasResponse
      ? 'Not answered'
      : 'No response',
    fteDelta,

    daysOffRequested,
    daysOffLabel,
    dayOffPreference,
    daysOffEntitled,
    daysOffNamed,
    daysOffUnnamed,
    awaitingDayChoice,

    needsDiscussion: ftePreference === 'discuss' || dayOffPreference === 'discuss',
    returning: intention?.intentionToReturn ?? null,
    hasLeavePlans: !!employment?.hasLeavePlans,
    leaveType: employment?.leaveType ?? null,
    leaveStartDate: employment?.leaveStartDate ?? null,
    leaveEndDate: employment?.leaveEndDate ?? null,
    leavePlans: availability?.leavePlans || '',
    comments: intention?.additionalComments || '',

    submissionStatus: intention?.status ?? null,
    hasResponse,
    isLate: !!intention?.isLate,
    submittedAt: intention?.submittedAt ?? null,
  };
}

function summarise(rows: FteDaysOffRow[]): FteDaysOffSummary {
  const summary: FteDaysOffSummary = {
    staffCount: rows.length,
    respondedCount: 0,
    noResponseCount: 0,
    draftOnlyCount: 0,
    currentFteTotal: 0,
    requestedFteTotal: 0,
    comparableCurrentFteTotal: 0,
    fteDeltaTotal: 0,
    reducingCount: 0,
    increasingCount: 0,
    unchangedCount: 0,
    unknownFteCount: 0,
    needsDiscussionCount: 0,
    notReturningCount: 0,
    uncertainCount: 0,
    leaveCount: 0,
    lateCount: 0,
    totalDaysOffRequested: 0,
    unnamedDaysOffTotal: 0,
    noDayNamedCount: 0,
    noDayNamedDaysTotal: 0,
    partlyNamedCount: 0,
    fteBands: [],
    weekdayDemand: WEEKDAYS.map(day => ({ day, count: 0 })),
    byDepartment: [],
  };

  const bandCounts = new Map<FtePreference, FteBandCount>();
  const weekdayIndex = new Map<Weekday, WeekdayDemand>(
    summary.weekdayDemand.map(d => [d.day, d])
  );
  const deptIndex = new Map<string, DepartmentSummary>();

  for (const row of rows) {
    const dept =
      deptIndex.get(row.department) ??
      {
        department: row.department,
        staffCount: 0,
        respondedCount: 0,
        currentFte: 0,
        requestedFte: 0,
        fteDelta: 0,
        daysOffDemand: 0,
        noDayNamedCount: 0,
        needsDiscussionCount: 0,
        notReturningCount: 0,
        noResponseCount: 0,
      };
    deptIndex.set(row.department, dept);

    dept.staffCount += 1;
    dept.currentFte += row.currentFte ?? 0;
    summary.currentFteTotal += row.currentFte ?? 0;

    if (row.hasResponse) {
      summary.respondedCount += 1;
      dept.respondedCount += 1;
    } else if (row.submissionStatus === 'draft') {
      summary.draftOnlyCount += 1;
      summary.noResponseCount += 1;
      dept.noResponseCount += 1;
    } else {
      summary.noResponseCount += 1;
      dept.noResponseCount += 1;
    }

    if (row.isLate) summary.lateCount += 1;
    if (row.needsDiscussion) {
      summary.needsDiscussionCount += 1;
      dept.needsDiscussionCount += 1;
    }
    if (row.returning === 'no') {
      summary.notReturningCount += 1;
      dept.notReturningCount += 1;
    }
    if (row.returning === 'uncertain') summary.uncertainCount += 1;
    if (row.hasLeavePlans) summary.leaveCount += 1;

    // Everything below is a planning number, so it counts submitted answers
    // only. A saved draft is not a response (same rule as intentionStats), and
    // half-filled answers would otherwise skew the totals and the day demand.
    if (!row.hasResponse) continue;

    // FTE movement — only comparable when both sides are known.
    if (row.requestedFte != null) {
      summary.requestedFteTotal += row.requestedFte;
      dept.requestedFte += row.requestedFte;
      if (row.currentFte != null) {
        summary.comparableCurrentFteTotal += row.currentFte;
        dept.fteDelta += row.requestedFte - row.currentFte;
      }
      if (row.fteDelta != null) {
        if (row.fteDelta < -0.001) summary.reducingCount += 1;
        else if (row.fteDelta > 0.001) summary.increasingCount += 1;
        else summary.unchangedCount += 1;
      }
    } else if (row.hasResponse) {
      summary.unknownFteCount += 1;
    }

    // Days-off demand.
    summary.totalDaysOffRequested += row.daysOffEntitled ?? 0;
    summary.unnamedDaysOffTotal += row.daysOffUnnamed;
    dept.daysOffDemand += row.daysOffEntitled ?? 0;
    if (row.awaitingDayChoice) {
      summary.noDayNamedCount += 1;
      summary.noDayNamedDaysTotal += row.daysOffEntitled ?? 0;
      dept.noDayNamedCount += 1;
    } else if (row.daysOffUnnamed > 0) {
      // Named a day, but fewer than the requested load implies.
      summary.partlyNamedCount += 1;
    }
    for (const day of row.daysOffRequested) {
      const entry = weekdayIndex.get(day);
      if (entry) entry.count += 1;
    }

    // Requested-load bands (responders only — "no response" isn't a band).
    if (row.ftePreference) {
      const band =
        bandCounts.get(row.ftePreference) ??
        {
          preference: row.ftePreference,
          label: FTE_LABELS[row.ftePreference],
          count: 0,
          fte: 0,
        };
      band.count += 1;
      band.fte += FTE_NUMERIC[row.ftePreference] ?? 0;
      bandCounts.set(row.ftePreference, band);
    }
  }

  summary.currentFteTotal = round2(summary.currentFteTotal);
  summary.requestedFteTotal = round2(summary.requestedFteTotal);
  summary.comparableCurrentFteTotal = round2(summary.comparableCurrentFteTotal);
  summary.fteDeltaTotal = round2(
    summary.requestedFteTotal - summary.comparableCurrentFteTotal
  );

  summary.fteBands = FTE_BAND_ORDER.filter(p => bandCounts.has(p)).map(p => {
    const band = bandCounts.get(p)!;
    return { ...band, fte: round2(band.fte) };
  });

  summary.byDepartment = Array.from(deptIndex.values())
    .map(d => ({
      ...d,
      currentFte: round2(d.currentFte),
      requestedFte: round2(d.requestedFte),
      fteDelta: round2(d.fteDelta),
    }))
    .sort((a, b) => a.department.localeCompare(b.department));

  return summary;
}

/**
 * One row per staff member with their contracted FTE, the load and day(s) off
 * they asked for, plus school-level and per-learning-area planning summaries.
 */
export async function getFteDaysOffReport(
  options: FteDaysOffOptions
): Promise<FteDaysOffReport> {
  const {
    year,
    department,
    staffGroup = 'teaching',
    includeNonResponders = true,
  } = options;

  const [allStaff, intentions] = await Promise.all([
    staffService.getAllStaff(),
    intentionsService.getIntentionsByYear(year),
  ]);

  const byCode = dedupeByStaffCode(intentions);

  // Name fallback for imported rows whose staff code doesn't match the roster.
  const byName = new Map<string, TeachingIntention>();
  for (const intention of byCode.values()) {
    const name = (intention.staffName || '').trim().toLowerCase();
    if (name && !byName.has(name)) byName.set(name, intention);
  }

  // Counted in stages so an empty result can say WHICH step emptied it.
  const groupMatches = allStaff.filter(s => isExpectedToSubmit(s, staffGroup));
  const roster = groupMatches.filter(s => {
    if (department && department !== 'all' && s.department !== department) return false;
    return true;
  });

  const rows = roster
    .map(staff => {
      const intention =
        byCode.get(getStaffCode(staff)) ??
        byName.get((staff.name || '').trim().toLowerCase());
      return buildRow(staff, intention);
    })
    .filter(row => includeNonResponders || row.hasResponse)
    .sort((a, b) => a.staffName.localeCompare(b.staffName));

  return {
    year,
    rows,
    summary: summarise(rows),
    rosterSourceCount: allStaff.length,
    groupMatchCount: groupMatches.length,
  };
}
